import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Inbox, ShoppingBag, Package, Settings as SettingsIcon, Tag, Search, ChevronRight, Plus, Trash2, MessageCircle as MsgIcon, Send, ExternalLink, Bot, Download, Key, Activity, Upload, FileText, BarChart3, CheckCircle2, XCircle, Star, MapPin, Edit2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { API_BASE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { formatINR } from "@/lib/utils-format";
import AdminConciergeInbox from "@/components/AdminConciergeInbox";
import ContentFactoryPanel from "@/components/ContentFactoryPanel";

const API_BASE_FOR_LINKS = API_BASE; // for sitemap.xml / robots.txt direct links

const TABS = [
    { id: "leads", label: "Leads", icon: Inbox },
    { id: "orders", label: "Orders", icon: ShoppingBag },
    { id: "concierge", label: "AI Inbox", icon: Bot },
    { id: "products", label: "Products", icon: Package },
    { id: "clinics", label: "Clinics", icon: MapPin },
    { id: "coupons", label: "Coupons", icon: Tag },
    { id: "reviews", label: "Reviews", icon: Star },
    { id: "content", label: "Content Factory", icon: Sparkles },
    { id: "seo", label: "SEO & Indexing", icon: Search },
    { id: "settings", label: "Settings", icon: SettingsIcon },
];

export default function AdminDashboard() {
    const { admin, logout } = useAuth();
    const navigate = useNavigate();
    const [tab, setTab] = useState("leads");
    const [leads, setLeads] = useState([]);
    const [leadFilter, setLeadFilter] = useState("all");
    const [orders, setOrders] = useState([]);
    const [products, setProducts] = useState([]);
    const [coupons, setCoupons] = useState([]);
    const [settings, setSettings] = useState(null);
    const [showCouponForm, setShowCouponForm] = useState(false);
    const [couponDraft, setCouponDraft] = useState({
        code: "",
        description: "",
        discount_type: "percent",
        value: 5,
        min_order_value: 0,
        max_discount: null,
        max_uses: null,
        expires_at: "",
        active: true,
    });

    const reloadCoupons = () => api.get("/admin/coupons").then((r) => setCoupons(r.data)).catch(() => {});

    useEffect(() => {
        document.title = "Admin · signiastylettoix.com";
        Promise.all([
            api.get("/admin/leads").then((r) => setLeads(r.data)).catch(() => {}),
            api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {}),
            api.get("/admin/products").then((r) => setProducts(r.data)).catch(() => {}),
            reloadCoupons(),
            api.get("/admin/settings").then((r) => setSettings(r.data)).catch(() => {}),
        ]);
    }, []);

    const onLogout = () => {
        logout();
        navigate("/admin/login");
    };

    const updateSettings = async () => {
        try {
            await api.put("/admin/settings", settings);
            toast.success("Settings saved");
        } catch {
            toast.error("Failed to save");
        }
    };

    const submitCoupon = async (e) => {
        e.preventDefault();
        const payload = {
            ...couponDraft,
            code: couponDraft.code.trim().toUpperCase(),
            value: parseInt(couponDraft.value) || 0,
            min_order_value: parseInt(couponDraft.min_order_value) || 0,
            max_discount: couponDraft.max_discount ? parseInt(couponDraft.max_discount) : null,
            max_uses: couponDraft.max_uses ? parseInt(couponDraft.max_uses) : null,
            expires_at: couponDraft.expires_at || null,
        };
        if (!payload.code) {
            toast.error("Code is required");
            return;
        }
        try {
            await api.post("/admin/coupons", payload);
            toast.success("Coupon created");
            setShowCouponForm(false);
            setCouponDraft({ code: "", description: "", discount_type: "percent", value: 5, min_order_value: 0, max_discount: null, max_uses: null, expires_at: "", active: true });
            reloadCoupons();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to create");
        }
    };

    const toggleCoupon = async (c) => {
        try {
            await api.put(`/admin/coupons/${c.code}`, { ...c, active: !c.active });
            reloadCoupons();
        } catch {
            toast.error("Failed to update");
        }
    };

    const deleteCoupon = async (code) => {
        if (!window.confirm(`Delete coupon ${code}?`)) return;
        try {
            await api.delete(`/admin/coupons/${code}`);
            toast.success("Coupon deleted");
            reloadCoupons();
        } catch {
            toast.error("Failed to delete");
        }
    };

    const filteredLeads = leadFilter === "all" ? leads : leads.filter((l) => l.type === leadFilter);
    const leadCounts = leads.reduce((acc, l) => ({ ...acc, [l.type]: (acc[l.type] || 0) + 1 }), {});

    // ---- SEO state ----
    const [seoPages, setSeoPages] = useState([]);
    const [seoBusy, setSeoBusy] = useState(false);
    const [seoResult, setSeoResult] = useState(null);
    const [seoStatus, setSeoStatus] = useState(null);
    const [cronStatus, setCronStatus] = useState(null);
    const [googleKeyJson, setGoogleKeyJson] = useState("");
    const [seoSearch, setSeoSearch] = useState("");
    const [localSeo, setLocalSeo] = useState(null);
    const [waveBusy, setWaveBusy] = useState(false);
    const [pendingReviews, setPendingReviews] = useState([]);

    const loadPendingReviews = () => api.get("/admin/reviews?status=pending").then((r) => setPendingReviews(r.data)).catch(() => {});
    useEffect(() => { if (tab === "reviews") loadPendingReviews(); }, [tab]);

    useEffect(() => {
        if (tab === "seo") {
            if (seoPages.length === 0) {
                api.get("/seo/pages").then((r) => setSeoPages(r.data)).catch(() => {});
            }
            api.get("/admin/seo/status").then((r) => setSeoStatus(r.data)).catch(() => {});
            api.get("/admin/local-seo/status").then((r) => setLocalSeo(r.data)).catch(() => {});
            api.get("/admin/seo/cron-status").then((r) => setCronStatus(r.data)).catch(() => {});
        }
    }, [tab, seoPages.length]);

    const saveAnalyticsAndVerification = async () => {
        try {
            await api.put("/admin/settings", settings);
            const r = await api.get("/admin/seo/status"); setSeoStatus(r.data);
            toast.success("SEO + Analytics config saved");
        } catch { toast.error("Failed to save"); }
    };

    const uploadGoogleKey = async () => {
        if (!googleKeyJson.trim()) { toast.error("Paste your service-account JSON"); return; }
        try {
            const r = await api.post("/admin/seo/google-indexing-key", { json_text: googleKeyJson });
            toast.success(`Google Indexing key saved (service account: ${r.data.client_email})`);
            setGoogleKeyJson("");
            const s = await api.get("/admin/seo/status"); setSeoStatus(s.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Invalid JSON");
        }
    };

    const regenerateSitemap = async () => {
        try {
            const r = await api.post("/admin/seo/regenerate-sitemap");
            toast.success(`Sitemap regenerated · ${r.data.sitemap_url_count} URLs written to /sitemap.xml`);
        } catch (e) {
            toast.error("Failed to regenerate sitemap");
        }
    };

    const clearGoogleKey = async () => {
        if (!window.confirm("Remove the Google Indexing API service-account JSON?")) return;
        await api.delete("/admin/seo/google-indexing-key");
        toast.success("Google Indexing key cleared");
        const s = await api.get("/admin/seo/status"); setSeoStatus(s.data);
    };

    const pushAllToIndex = async () => {
        setSeoBusy(true); setSeoResult(null);
        try {
            const { data } = await api.get("/admin/seo/all-urls");
            const { data: result } = await api.post("/admin/seo/index-now", { urls: data.urls });
            setSeoResult({ count: data.urls.length, ...result });
            toast.success(`Submitted ${data.urls.length} URLs to IndexNow${result.google?.ok ? " + Google" : ""}`);
        } catch (e) {
            toast.error("Indexing push failed");
        } finally { setSeoBusy(false); }
    };

    const pushOneUrl = async (url) => {
        setSeoBusy(true);
        try {
            const { data: result } = await api.post("/admin/seo/index-now", { urls: [url] });
            setSeoResult({ count: 1, single: url, ...result });
            toast.success("Pushed to IndexNow" + (result.google?.ok ? " + Google" : ""));
        } catch { toast.error("Push failed"); } finally { setSeoBusy(false); }
    };

    return (
        <div className="min-h-screen bg-[#f8f9fa]">
            <header className="bg-white border-b border-[#E5E7EB] sticky top-0 z-30">
                <div className="container-x flex items-center justify-between h-16">
                    <div className="flex items-center gap-6">
                        <Link to="/" className="font-serif text-[22px]">signiastyletto<span className="text-[#003E4D]">ix</span></Link>
                        <span className="text-[12px] text-[#9CA3AF] uppercase tracking-[0.18em]">Admin</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <span className="text-[13px] text-[#4a5568]" data-testid="admin-email-display">{admin?.email}</span>
                        <button onClick={onLogout} className="btn-ghost flex items-center gap-2" data-testid="admin-logout">
                            <LogOut size={14} /> Log out
                        </button>
                    </div>
                </div>
            </header>

            <div className="container-x py-10 grid lg:grid-cols-12 gap-8">
                <aside className="lg:col-span-3">
                    <nav className="bg-white rounded-2xl p-3 border border-black/5">
                        {TABS.map((t) => (
                            <button
                                key={t.id}
                                onClick={() => setTab(t.id)}
                                data-testid={`admin-tab-${t.id}`}
                                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-[14px] transition ${tab === t.id ? "bg-[#003E4D] text-white" : "text-[#1a1a1a] hover:bg-black/5"}`}
                            >
                                <t.icon size={16} /> {t.label}
                                <ChevronRight size={14} className="ml-auto opacity-50" />
                            </button>
                        ))}
                    </nav>
                    <div className="mt-6 grid grid-cols-2 gap-3">
                        <Stat label="Leads" value={leads.length} />
                        <Stat label="Orders" value={orders.length} />
                    </div>
                </aside>

                <section className="lg:col-span-9 bg-white rounded-2xl border border-black/5 p-6 md:p-8">
                    {tab === "leads" && (
                        <div data-testid="admin-leads-panel">
                            <div className="flex items-center justify-between flex-wrap gap-3">
                                <h2 className="font-serif text-[28px]">Leads ({filteredLeads.length})</h2>
                                <div className="flex flex-wrap items-center gap-1.5">
                                    {[
                                        { id: "all", label: "All" },
                                        { id: "whatsapp", label: "WhatsApp", icon: MsgIcon },
                                        { id: "upgrade", label: "Upgrade" },
                                        { id: "trial", label: "Trial" },
                                        { id: "callback", label: "Callback" },
                                        { id: "hearing_test", label: "Hearing Test" },
                                        { id: "repair", label: "Repair" },
                                        { id: "general", label: "General" },
                                    ].map((f) => (
                                        <button
                                            key={f.id}
                                            onClick={() => setLeadFilter(f.id)}
                                            data-testid={`leads-filter-${f.id}`}
                                            className={`px-3 py-1.5 rounded-full text-[12px] font-medium transition border ${leadFilter === f.id ? "bg-[#003E4D] text-white border-[#003E4D]" : "border-[#E5E7EB] text-[#4a5568] hover:border-[#003E4D]"}`}
                                        >
                                            {f.label}{f.id !== "all" && leadCounts[f.id] ? ` · ${leadCounts[f.id]}` : ""}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="mt-6 overflow-x-auto">
                                <table className="w-full text-left min-w-[900px]">
                                    <thead className="text-[12px] uppercase tracking-[0.18em] text-[#9CA3AF]">
                                        <tr className="border-b border-[#E5E7EB]"><th className="py-3 pr-4">Type</th><th className="py-3 pr-4">Name</th><th className="py-3 pr-4">Mobile</th><th className="py-3 pr-4">City</th><th className="py-3 pr-4">Product / Details</th><th className="py-3 pr-4">Date</th></tr>
                                    </thead>
                                    <tbody>
                                        {filteredLeads.map((l) => (
                                            <LeadRow key={l.id} l={l} />
                                        ))}
                                        {filteredLeads.length === 0 && <tr><td colSpan={6} className="py-12 text-center text-[#9CA3AF]">No leads in this filter.</td></tr>}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {tab === "orders" && (
                        <div data-testid="admin-orders-panel">
                            <h2 className="font-serif text-[28px]">Orders ({orders.length})</h2>
                            <div className="mt-6 overflow-x-auto">
                                <table className="w-full text-left min-w-[700px]">
                                    <thead className="text-[12px] uppercase tracking-[0.18em] text-[#9CA3AF]">
                                        <tr className="border-b border-[#E5E7EB]"><th className="py-3 pr-4">Order #</th><th className="py-3 pr-4">Customer</th><th className="py-3 pr-4">Total</th><th className="py-3 pr-4">Payment</th><th className="py-3 pr-4">Status</th><th className="py-3 pr-4">Date</th></tr>
                                    </thead>
                                    <tbody>
                                        {orders.map((o) => (
                                            <tr key={o.id} className="border-b border-[#E5E7EB] text-[13.5px]" data-testid={`order-row-${o.id}`}>
                                                <td className="py-3 pr-4 font-mono text-[12px]">{o.order_number}</td>
                                                <td className="py-3 pr-4 font-medium">{o.customer_name}<div className="text-[11.5px] text-[#9CA3AF]">{o.customer_mobile}</div></td>
                                                <td className="py-3 pr-4 font-semibold">{formatINR(o.total)}</td>
                                                <td className="py-3 pr-4 capitalize">{o.payment_method}</td>
                                                <td className="py-3 pr-4 capitalize">{o.status}</td>
                                                <td className="py-3 pr-4 text-[#4a5568]">{new Date(o.created_at).toLocaleDateString("en-IN")}</td>
                                            </tr>
                                        ))}
                                        {orders.length === 0 && <tr><td colSpan={6} className="py-12 text-center text-[#9CA3AF]">No orders yet.</td></tr>}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {tab === "concierge" && <AdminConciergeInbox />}

                    {tab === "products" && (
                        <div data-testid="admin-products-panel">
                            <div className="flex items-center justify-between flex-wrap gap-3">
                                <div>
                                    <h2 className="font-serif text-[28px]">Products ({products.length})</h2>
                                    <p className="text-[14px] text-[#4a5568] mt-1">Live Styletto catalog synced from HearClear.</p>
                                </div>
                                <button
                                    onClick={async () => {
                                        try {
                                            const r = await api.post("/admin/sync-styletto", { replace: true });
                                            toast.success(`Synced ${r.data.imported} Styletto products from HearClear`);
                                            const fresh = await api.get("/admin/products");
                                            setProducts(fresh.data);
                                        } catch { toast.error("Sync failed"); }
                                    }}
                                    className="btn-primary !text-[13px] !py-2.5 !px-5"
                                    data-testid="sync-styletto-btn"
                                >
                                    <Download size={14} /> Sync from HearClear
                                </button>
                            </div>
                            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                                {products.map((p) => (
                                    <div key={p.slug} className="border border-black/5 rounded-xl p-4 flex gap-4 items-center" data-testid={`admin-product-${p.slug}`}>
                                        <img src={p.image} alt={p.name} className="w-16 h-16 object-contain bg-[#f3f4f6] rounded-lg" />
                                        <div className="flex-1">
                                            <p className="font-medium text-[14px]">{p.short_name}</p>
                                            <p className="text-[12px] text-[#4a5568]">{p.technology_level}</p>
                                            <p className="text-[13px] font-semibold mt-1">{formatINR(p.price)}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {tab === "clinics" && <ClinicsPanel />}
                    {tab === "content" && <ContentFactoryPanel />}

                    {tab === "coupons" && (
                        <div data-testid="admin-coupons-panel">
                            <div className="flex items-center justify-between flex-wrap gap-3">
                                <div>
                                    <h2 className="font-serif text-[28px]">Coupons ({coupons.length})</h2>
                                    <p className="text-[14px] text-[#4a5568] mt-1">Create discount codes redeemable at checkout.</p>
                                </div>
                                <button onClick={() => setShowCouponForm((s) => !s)} className="btn-primary !text-[13px] !py-2.5 !px-5" data-testid="coupon-new-btn">
                                    <Plus size={14} /> {showCouponForm ? "Close" : "New Coupon"}
                                </button>
                            </div>

                            {showCouponForm && (
                                <form onSubmit={submitCoupon} className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4 p-6 bg-[#f8f9fa] rounded-2xl border border-black/5" data-testid="coupon-form">
                                    <CField label="Code (uppercase)" testid="coupon-code">
                                        <input required value={couponDraft.code} onChange={(e) => setCouponDraft({ ...couponDraft, code: e.target.value.toUpperCase() })} className="h-11 w-full px-3 rounded-lg border border-[#E5E7EB] bg-white text-[14px] uppercase font-mono" />
                                    </CField>
                                    <CField label="Discount type" testid="coupon-type">
                                        <select value={couponDraft.discount_type} onChange={(e) => setCouponDraft({ ...couponDraft, discount_type: e.target.value })} className="h-11 w-full px-3 rounded-lg border border-[#E5E7EB] bg-white text-[14px]">
                                            <option value="percent">Percent off (%)</option>
                                            <option value="flat">Flat amount off (₹)</option>
                                            <option value="upgrade_credit">Upgrade credit (% of subtotal)</option>
                                        </select>
                                    </CField>
                                    <CField label="Value" testid="coupon-value">
                                        <input required type="number" value={couponDraft.value} onChange={(e) => setCouponDraft({ ...couponDraft, value: e.target.value })} className="h-11 w-full px-3 rounded-lg border border-[#E5E7EB] bg-white text-[14px]" />
                                    </CField>
                                    <CField label="Min order ₹" testid="coupon-min">
                                        <input type="number" value={couponDraft.min_order_value} onChange={(e) => setCouponDraft({ ...couponDraft, min_order_value: e.target.value })} className="h-11 w-full px-3 rounded-lg border border-[#E5E7EB] bg-white text-[14px]" />
                                    </CField>
                                    <CField label="Max discount cap ₹ (optional)" testid="coupon-max">
                                        <input type="number" value={couponDraft.max_discount || ""} onChange={(e) => setCouponDraft({ ...couponDraft, max_discount: e.target.value })} className="h-11 w-full px-3 rounded-lg border border-[#E5E7EB] bg-white text-[14px]" />
                                    </CField>
                                    <CField label="Max uses (optional)" testid="coupon-uses">
                                        <input type="number" value={couponDraft.max_uses || ""} onChange={(e) => setCouponDraft({ ...couponDraft, max_uses: e.target.value })} className="h-11 w-full px-3 rounded-lg border border-[#E5E7EB] bg-white text-[14px]" />
                                    </CField>
                                    <CField label="Expires (optional)" testid="coupon-expires">
                                        <input type="date" value={couponDraft.expires_at?.slice(0, 10) || ""} onChange={(e) => setCouponDraft({ ...couponDraft, expires_at: e.target.value ? `${e.target.value}T23:59:59+05:30` : "" })} className="h-11 w-full px-3 rounded-lg border border-[#E5E7EB] bg-white text-[14px]" />
                                    </CField>
                                    <CField label="Description (shown to admin)" testid="coupon-desc" wide>
                                        <input value={couponDraft.description} onChange={(e) => setCouponDraft({ ...couponDraft, description: e.target.value })} className="h-11 w-full px-3 rounded-lg border border-[#E5E7EB] bg-white text-[14px]" />
                                    </CField>
                                    <div className="md:col-span-2">
                                        <button type="submit" className="btn-primary" data-testid="coupon-submit">
                                            Create coupon <Plus size={14} />
                                        </button>
                                    </div>
                                </form>
                            )}

                            <div className="mt-6 overflow-x-auto">
                                <table className="w-full text-left min-w-[760px]">
                                    <thead className="text-[12px] uppercase tracking-[0.18em] text-[#9CA3AF]">
                                        <tr className="border-b border-[#E5E7EB]"><th className="py-3 pr-4">Code</th><th className="py-3 pr-4">Type</th><th className="py-3 pr-4">Value</th><th className="py-3 pr-4">Min order</th><th className="py-3 pr-4">Used</th><th className="py-3 pr-4">Status</th><th className="py-3 pr-4">Actions</th></tr>
                                    </thead>
                                    <tbody>
                                        {coupons.map((c) => (
                                            <tr key={c.code} className="border-b border-[#E5E7EB] text-[13.5px]" data-testid={`coupon-row-${c.code}`}>
                                                <td className="py-3 pr-4 font-mono font-semibold">{c.code}<div className="text-[11px] text-[#9CA3AF] font-sans font-normal mt-0.5">{c.description || "—"}</div></td>
                                                <td className="py-3 pr-4 capitalize">{c.discount_type.replace("_", " ")}</td>
                                                <td className="py-3 pr-4 font-semibold">
                                                    {c.discount_type === "flat" ? formatINR(c.value) : `${c.value}%`}
                                                    {c.max_discount && <span className="text-[11px] text-[#9CA3AF] block font-normal">cap {formatINR(c.max_discount)}</span>}
                                                </td>
                                                <td className="py-3 pr-4">{c.min_order_value ? formatINR(c.min_order_value) : "—"}</td>
                                                <td className="py-3 pr-4">{c.used_count}{c.max_uses ? ` / ${c.max_uses}` : ""}</td>
                                                <td className="py-3 pr-4">
                                                    <button onClick={() => toggleCoupon(c)} className={`px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider ${c.active ? "bg-[#003E4D]/10 text-[#003E4D]" : "bg-[#9CA3AF]/15 text-[#4a5568]"}`} data-testid={`coupon-toggle-${c.code}`}>
                                                        {c.active ? "Active" : "Inactive"}
                                                    </button>
                                                </td>
                                                <td className="py-3 pr-4">
                                                    <button onClick={() => deleteCoupon(c.code)} className="text-[#9CA3AF] hover:text-red-500" data-testid={`coupon-delete-${c.code}`}>
                                                        <Trash2 size={14} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                        {coupons.length === 0 && <tr><td colSpan={7} className="py-12 text-center text-[#9CA3AF]">No coupons yet.</td></tr>}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {tab === "reviews" && (
                        <div data-testid="admin-reviews-panel">
                            <h2 className="font-serif text-[24px] mb-1">Review moderation</h2>
                            <p className="text-[13px] text-[#4a5568] mb-5">{pendingReviews.length} pending review{pendingReviews.length === 1 ? "" : "s"} awaiting approval.</p>
                            {pendingReviews.length === 0 && <p className="text-[14px] text-[#9CA3AF]">Nothing pending — approved reviews appear live on product pages.</p>}
                            <div className="space-y-4">
                                {pendingReviews.map((r) => (
                                    <div key={r.id} className="p-5 bg-white border border-[#e2ddd4] rounded-2xl" data-testid={`pending-review-${r.id}`}>
                                        <div className="flex items-center justify-between flex-wrap gap-2">
                                            <p className="font-medium text-[15px]">{r.title} <span className="text-[#B8860B]">({r.rating}★)</span></p>
                                            <span className="text-[12px] text-[#4a5568]">{r.product_slug}</span>
                                        </div>
                                        <p className="mt-2 text-[14px] text-[#333]">{r.body}</p>
                                        <p className="mt-2 text-[12.5px] text-[#4a5568]">{r.name}{r.city ? ` · ${r.city}` : ""}</p>
                                        <div className="mt-3 flex gap-2">
                                            <button onClick={async () => { await api.post(`/admin/reviews/${r.id}/moderate`, { action: "approve" }); toast.success("Approved"); loadPendingReviews(); }}
                                                className="text-[12.5px] px-4 py-1.5 rounded-full bg-[#1a5c3a] text-white" data-testid={`approve-review-${r.id}`}>Approve</button>
                                            <button onClick={async () => { await api.post(`/admin/reviews/${r.id}/moderate`, { action: "reject" }); toast.success("Rejected"); loadPendingReviews(); }}
                                                className="text-[12.5px] px-4 py-1.5 rounded-full border border-[#c0392b] text-[#c0392b]" data-testid={`reject-review-${r.id}`}>Reject</button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {tab === "seo" && (
                        <div data-testid="admin-seo-panel">
                            <div className="flex items-center justify-between flex-wrap gap-3">
                                <div>
                                    <h2 className="font-serif text-[28px]">SEO &amp; Search Indexing</h2>
                                    <p className="text-[14px] text-[#4a5568] mt-1">{seoPages.length} programmatic SEO pages live. Configure analytics, verify ownership, and push URLs to Google/Bing in one click.</p>
                                </div>
                                <div className="flex items-center gap-2 flex-wrap">
                                <button onClick={regenerateSitemap} className="text-[12px] inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border-2 border-[#003E4D] text-[#003E4D] hover:bg-[#003E4D] hover:text-white transition" data-testid="seo-regenerate-sitemap">
                                    <FileText size={12} /> Regenerate Sitemap
                                </button>
                                <button onClick={pushAllToIndex} disabled={seoBusy} className="btn-primary !text-[13px] !py-2.5 !px-5" data-testid="seo-push-all">
                                    <Send size={14} /> {seoBusy ? "Pushing…" : "Push ALL URLs to Index"}
                                </button>
                                </div>
                            </div>

                            {/* Status grid */}
                            {seoStatus && (
                                <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="seo-status-grid">
                                    <StatusCard ok={seoStatus.ga4_configured} label="GA4" detail={seoStatus.ga4_configured ? "Configured" : "Not set"} icon={BarChart3} />
                                    <StatusCard ok={seoStatus.meta_pixel_configured} label="Meta Pixel" detail={seoStatus.meta_pixel_configured ? "Configured" : "Not set"} icon={Activity} />
                                    <StatusCard ok={seoStatus.gsc_verified} label="Google SC" detail={seoStatus.gsc_verified ? "Verified" : "Not verified"} icon={Search} />
                                    <StatusCard ok={seoStatus.bing_verified} label="Bing Webmaster" detail={seoStatus.bing_verified ? "Verified" : "Not verified"} icon={Search} />
                                    <StatusCard ok={seoStatus.bing_webmaster_api_configured} label="Bing API" detail={seoStatus.bing_webmaster_api_configured ? "Active — auto-submit enabled" : "API key not set"} icon={Send} />
                                    <StatusCard ok={true} label="IndexNow Key" detail={seoStatus.indexnow_key} icon={Key} />
                                    <StatusCard ok={seoStatus.google_indexing_enabled} label="Google Indexing API" detail={seoStatus.google_indexing_client_email || (seoStatus.google_indexing_enabled ? "Enabled" : "JSON not uploaded")} icon={Key} />
                                    <StatusCard ok={true} label="Sitemap" detail={`${seoStatus.seo_page_count + seoStatus.product_count + 6} URLs`} icon={FileText} />
                                    <StatusCard ok={true} label="Products in catalog" detail={`${seoStatus.product_count} SKUs`} icon={Package} />
                                </div>
                            )}

                            {/* Local SEO engine (50k+ pages) */}
                            {localSeo && (
                                <div className="mt-7 p-5 bg-[#f0f7f4] rounded-2xl border border-[#cfe3d8]" data-testid="local-seo-panel">
                                    <h3 className="font-serif text-[18px] flex items-center gap-2"><Search size={16} className="text-[#003E4D]" /> Programmatic Local SEO Engine</h3>
                                    <p className="text-[12px] text-[#4a5568] mt-1">
                                        {localSeo.total_pages.toLocaleString("en-IN")} unique 2,000-word pages live across {localSeo.cities} cities, {localSeo.areas} areas and {localSeo.pins.toLocaleString("en-IN")} PIN codes × {localSeo.products_in_matrix} products.
                                        Sitemap release is phased to avoid Google spam-flagging a sudden URL dump.
                                    </p>
                                    <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
                                        <div className="bg-white rounded-xl p-3 border border-black/5"><div className="text-[20px] font-serif text-[#003E4D]">{localSeo.total_pages.toLocaleString("en-IN")}</div><div className="text-[11px] text-[#4a5568]">Total pages servable</div></div>
                                        <div className="bg-white rounded-xl p-3 border border-black/5"><div className="text-[20px] font-serif text-[#003E4D]">Wave {localSeo.wave}</div><div className="text-[11px] text-[#4a5568]">Current release wave</div></div>
                                        <div className="bg-white rounded-xl p-3 border border-black/5"><div className="text-[20px] font-serif text-[#003E4D]">{Math.min(localSeo.wave_limit, localSeo.total_pages).toLocaleString("en-IN")}</div><div className="text-[11px] text-[#4a5568]">URLs in sitemaps</div></div>
                                        <div className="bg-white rounded-xl p-3 border border-black/5"><div className="text-[20px] font-serif text-[#003E4D]">{localSeo.sitemap_files.length}</div><div className="text-[11px] text-[#4a5568]">Sitemap files</div></div>
                                    </div>
                                    <div className="mt-4 flex items-center gap-2 flex-wrap">
                                        {[{ w: 1, label: "Wave 1 — 5,000 URLs" }, { w: 2, label: "Wave 2 — 20,000 URLs" }, { w: 3, label: "Wave 3 — ALL pages" }].map(({ w, label }) => (
                                            <button key={w} disabled={waveBusy}
                                                onClick={async () => {
                                                    setWaveBusy(true);
                                                    try {
                                                        const { data } = await api.post("/admin/local-seo/set-wave", { wave: w });
                                                        toast.success(`Wave ${w} released: ${data.released_urls.toLocaleString("en-IN")} URLs in sitemaps`);
                                                        const s = await api.get("/admin/local-seo/status"); setLocalSeo(s.data);
                                                    } catch { toast.error("Wave update failed"); } finally { setWaveBusy(false); }
                                                }}
                                                data-testid={`local-seo-wave-${w}`}
                                                className={`text-[12px] px-3 py-1.5 rounded-full border-2 transition ${localSeo.wave === w ? "bg-[#003E4D] text-white border-[#003E4D]" : "border-[#003E4D] text-[#003E4D] hover:bg-[#003E4D] hover:text-white"}`}>
                                                {label}
                                            </button>
                                        ))}
                                    </div>
                                    <p className="text-[11.5px] text-[#4a5568] mt-3">Recommended: stay on Wave 1 for ~3 weeks, move to Wave 2 once Google Search Console shows the first batch indexing, then Wave 3. The daily cron already pushes these URLs to Bing/IndexNow automatically.</p>
                                </div>
                            )}
                            {settings && (
                                <div className="mt-7 p-5 bg-[#f8f9fa] rounded-2xl border border-black/5" data-testid="seo-analytics-block">
                                    <h3 className="font-serif text-[18px] flex items-center gap-2"><BarChart3 size={16} className="text-[#003E4D]" /> Analytics &amp; Verification</h3>
                                    <p className="text-[12px] text-[#4a5568] mt-1">Configure GA4 + Meta Pixel for conversion tracking, and paste verification tokens for Google Search Console + Bing Webmaster Tools.</p>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                        <Field label="GA4 Measurement ID (G-XXXXXXX)" value={settings.ga4_measurement_id || ""} onChange={(v) => setSettings({ ...settings, ga4_measurement_id: v })} testid="seo-ga4" />
                                        <Field label="Meta (Facebook) Pixel ID" value={settings.meta_pixel_id || ""} onChange={(v) => setSettings({ ...settings, meta_pixel_id: v })} testid="seo-pixel" />
                                        <Field label="Google Search Console verification (content value only)" value={settings.gsc_verification || ""} onChange={(v) => setSettings({ ...settings, gsc_verification: v })} testid="seo-gsc" wide />
                                        <Field label="Bing Webmaster verification (msvalidate.01 value)" value={settings.bing_verification || ""} onChange={(v) => setSettings({ ...settings, bing_verification: v })} testid="seo-bing" wide />
                                        <Field label="Canonical site base URL" value={settings.site_base_url || "https://signiastylettoix.com"} onChange={(v) => setSettings({ ...settings, site_base_url: v })} testid="seo-base-url" wide />
                                    </div>
                                    <p className="text-[11px] text-[#9CA3AF] mt-3">Tip: paste only the <code className="bg-white px-1">content="…"</code> value from your meta tag, not the whole tag.</p>
                                    <div className="flex items-center gap-3 mt-4">
                                        <button onClick={saveAnalyticsAndVerification} className="btn-primary !text-[13px] !py-2 !px-4" data-testid="seo-save-analytics">
                                            Save Analytics &amp; Verification
                                        </button>
                                        <span className="text-[11px] text-[#9CA3AF]">Changes appear on the live site after a hard refresh.</span>
                                    </div>
                                </div>
                            )}

                            {/* Daily Indexing Cron block */}
                            <div className="mt-6 p-5 bg-[#f8f9fa] rounded-2xl border border-black/5" data-testid="seo-cron-block">
                                <h3 className="font-serif text-[18px] flex items-center gap-2"><Activity size={16} className="text-[#003E4D]" /> Daily Auto-Indexing</h3>
                                <p className="text-[12px] text-[#4a5568] mt-1">A background job submits 100 fresh URLs/day to Bing Webmaster API (09:00 IST) + IndexNow (09:05 IST), rotating across all 600+ site URLs. Sitemap auto-regenerates every night at 03:00 IST.</p>
                                <div className="mt-3 flex items-center gap-3 flex-wrap">
                                    <button onClick={async () => { try { const r = await api.post("/admin/seo/cron/run-bing-now"); toast.success("Bing batch submitted now"); console.log(r.data); const s = await api.get("/admin/seo/cron-status"); setCronStatus(s.data); } catch { toast.error("Failed"); } }} className="btn-primary !text-[13px] !py-2 !px-4" data-testid="seo-run-bing-now">
                                        <Send size={14} /> Run Bing submission NOW
                                    </button>
                                    <button onClick={async () => { try { await api.post("/admin/seo/cron/run-indexnow-now"); toast.success("IndexNow batch submitted now"); const s = await api.get("/admin/seo/cron-status"); setCronStatus(s.data); } catch { toast.error("Failed"); } }} className="text-[12px] inline-flex items-center gap-1.5 px-3 py-2 rounded-full border-2 border-[#003E4D] text-[#003E4D] hover:bg-[#003E4D] hover:text-white" data-testid="seo-run-indexnow-now">
                                        <Send size={12} /> Run IndexNow NOW
                                    </button>
                                </div>
                                {cronStatus && (
                                    <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-[12px]">
                                        <div className="bg-white p-3 rounded-lg border border-[#E5E7EB]"><div className="text-[10px] uppercase tracking-[0.18em] text-[#9CA3AF]">Tracked URLs</div><div className="font-semibold mt-1">{cronStatus.tracked_urls}</div></div>
                                        <div className="bg-white p-3 rounded-lg border border-[#E5E7EB]"><div className="text-[10px] uppercase tracking-[0.18em] text-[#9CA3AF]">Submitted today</div><div className="font-semibold mt-1">{cronStatus.submitted_today}</div></div>
                                        <div className="bg-white p-3 rounded-lg border border-[#E5E7EB]"><div className="text-[10px] uppercase tracking-[0.18em] text-[#9CA3AF]">Daily budget</div><div className="font-semibold mt-1">{cronStatus.daily_budget}</div></div>
                                        <div className="bg-white p-3 rounded-lg border border-[#E5E7EB]"><div className="text-[10px] uppercase tracking-[0.18em] text-[#9CA3AF]">Last run</div><div className="font-semibold mt-1 truncate" title={cronStatus.last_submission_at || ""}>{cronStatus.last_submission_at ? new Date(cronStatus.last_submission_at).toLocaleString() : "—"}</div></div>
                                    </div>
                                )}
                            </div>

                            {/* Google Indexing API JSON upload */}
                            <div className="mt-6 p-5 bg-[#f8f9fa] rounded-2xl border border-black/5" data-testid="seo-google-key-block">
                                <h3 className="font-serif text-[18px] flex items-center gap-2"><Key size={16} className="text-[#003E4D]" /> Google Indexing API</h3>
                                <p className="text-[12px] text-[#4a5568] mt-1">Paste the JSON service-account key from your Google Cloud project (Service Accounts → Keys → Create key). The key never leaves this database.</p>
                                {seoStatus?.google_indexing_enabled ? (
                                    <div className="mt-3 flex items-center justify-between p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                                        <div className="text-[13px] text-emerald-900">
                                            ✓ Active · service account: <code className="bg-white/60 px-1">{seoStatus.google_indexing_client_email || "configured"}</code>
                                        </div>
                                        <button onClick={clearGoogleKey} className="text-[12px] text-rose-600 hover:underline" data-testid="seo-clear-google-key">Clear key</button>
                                    </div>
                                ) : (
                                    <p className="mt-3 text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">Not configured — IndexNow (Bing) still works without this. Upload to enable Google direct indexing.</p>
                                )}
                                <textarea
                                    value={googleKeyJson}
                                    onChange={(e) => setGoogleKeyJson(e.target.value)}
                                    rows={6}
                                    placeholder='{ "type": "service_account", "project_id": "…", "private_key_id": "…", "private_key": "…", "client_email": "…@…iam.gserviceaccount.com", … }'
                                    className="mt-3 w-full font-mono text-[12px] border border-[#E5E7EB] rounded-lg p-3 focus:outline-none focus:border-[#003E4D]"
                                    data-testid="seo-google-key-input"
                                />
                                <button onClick={uploadGoogleKey} className="btn-primary mt-3 !text-[13px] !py-2 !px-4" data-testid="seo-google-key-upload">
                                    <Upload size={14} /> Upload Google Indexing JSON
                                </button>
                            </div>

                            {seoResult && (
                                <div className="mt-5 p-4 rounded-xl bg-[#f8f9fa] border border-black/5 text-[12.5px] font-mono whitespace-pre-wrap max-h-[280px] overflow-auto" data-testid="seo-result">
                                    {JSON.stringify(seoResult, null, 2)}
                                </div>
                            )}

                            {/* Quick links */}
                            <div className="mt-7 grid grid-cols-1 md:grid-cols-4 gap-3">
                                <a href={`${API_BASE_FOR_LINKS}/sitemap.xml`} target="_blank" rel="noreferrer" className="bg-[#f8f9fa] p-4 rounded-xl border border-black/5 hover:border-[#003E4D] transition flex items-center gap-3" data-testid="seo-link-sitemap">
                                    <ExternalLink size={16} className="text-[#003E4D]" />
                                    <div><p className="text-[13px] font-semibold">/sitemap.xml</p><p className="text-[11px] text-[#9CA3AF]">{seoStatus ? `${seoStatus.seo_page_count + seoStatus.product_count + 6} URLs` : "Auto-generated"}</p></div>
                                </a>
                                <a href={`${API_BASE_FOR_LINKS}/robots.txt`} target="_blank" rel="noreferrer" className="bg-[#f8f9fa] p-4 rounded-xl border border-black/5 hover:border-[#003E4D] transition flex items-center gap-3" data-testid="seo-link-robots">
                                    <ExternalLink size={16} className="text-[#003E4D]" />
                                    <div><p className="text-[13px] font-semibold">/robots.txt</p><p className="text-[11px] text-[#9CA3AF]">Auto-generated</p></div>
                                </a>
                                <a href="https://search.google.com/search-console" target="_blank" rel="noreferrer" className="bg-[#f8f9fa] p-4 rounded-xl border border-black/5 hover:border-[#003E4D] transition flex items-center gap-3">
                                    <ExternalLink size={16} className="text-[#003E4D]" />
                                    <div><p className="text-[13px] font-semibold">Google Search Console</p><p className="text-[11px] text-[#9CA3AF]">Verify + submit sitemap</p></div>
                                </a>
                                <a href="https://www.bing.com/webmasters" target="_blank" rel="noreferrer" className="bg-[#f8f9fa] p-4 rounded-xl border border-black/5 hover:border-[#003E4D] transition flex items-center gap-3">
                                    <ExternalLink size={16} className="text-[#003E4D]" />
                                    <div><p className="text-[13px] font-semibold">Bing Webmaster</p><p className="text-[11px] text-[#9CA3AF]">Verify + submit sitemap</p></div>
                                </a>
                            </div>

                            {/* SEO pages list with search */}
                            <div className="mt-7">
                                <div className="flex items-center justify-between flex-wrap gap-3">
                                    <h3 className="font-serif text-[20px]">SEO Pages ({seoPages.length})</h3>
                                    <input
                                        value={seoSearch}
                                        onChange={(e) => setSeoSearch(e.target.value)}
                                        placeholder="Filter by city, slug, or H1…"
                                        className="h-10 px-3 rounded-lg border border-[#E5E7EB] text-[13px] focus:outline-none focus:border-[#003E4D] w-full md:w-[280px]"
                                        data-testid="seo-search-input"
                                    />
                                </div>
                                <div className="mt-4 overflow-x-auto">
                                    <table className="w-full text-left min-w-[700px]">
                                        <thead className="text-[12px] uppercase tracking-[0.18em] text-[#9CA3AF]">
                                            <tr className="border-b border-[#E5E7EB]"><th className="py-3 pr-4">Page</th><th className="py-3 pr-4">Slug</th><th className="py-3 pr-4">Type</th><th className="py-3 pr-4">Action</th></tr>
                                        </thead>
                                        <tbody>
                                            {seoPages.filter((p) => {
                                                if (!seoSearch.trim()) return true;
                                                const q = seoSearch.toLowerCase();
                                                return p.slug.toLowerCase().includes(q) || (p.h1 || "").toLowerCase().includes(q) || (p.city || "").toLowerCase().includes(q);
                                            }).slice(0, 200).map((p) => (
                                                <tr key={p.slug} className="border-b border-[#E5E7EB] text-[13.5px]" data-testid={`seo-row-${p.slug}`}>
                                                    <td className="py-3 pr-4 font-medium">{p.h1}<div className="text-[11px] text-[#9CA3AF] font-normal mt-0.5">{p.title}</div></td>
                                                    <td className="py-3 pr-4 font-mono text-[12px]">/seo/{p.slug}</td>
                                                    <td className="py-3 pr-4">{p.city ? <span className="px-2 py-1 rounded-full bg-[#7DCDD2]/15 text-[#003E4D] text-[11px]">{p.city}</span> : <span className="text-[12px] text-[#4a5568]">Keyword</span>}</td>
                                                    <td className="py-3 pr-4 flex items-center gap-2">
                                                        <a href={`/seo/${p.slug}`} target="_blank" rel="noreferrer" className="text-[#003E4D] hover:underline text-[12px] inline-flex items-center gap-1"><ExternalLink size={12} /> View</a>
                                                        <button onClick={() => pushOneUrl(`${seoStatus?.site_base || "https://signiastylettoix.com"}/seo/${p.slug}`)} className="text-[#003E4D] hover:underline text-[12px] inline-flex items-center gap-1" data-testid={`seo-push-${p.slug}`}>
                                                            <Send size={12} /> Push
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}

                    {tab === "settings" && settings && (
                        <div data-testid="admin-settings-panel">
                            <h2 className="font-serif text-[28px]">Site Settings</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                                <Field label="WhatsApp Number (without +)" value={settings.whatsapp_number} onChange={(v) => setSettings({ ...settings, whatsapp_number: v })} testid="settings-wa" />
                                <Field label="Call Number" value={settings.call_number} onChange={(v) => setSettings({ ...settings, call_number: v })} testid="settings-call" />
                                <Field label="Contact Email" value={settings.contact_email} onChange={(v) => setSettings({ ...settings, contact_email: v })} testid="settings-email" />
                                <Field label="Upgrade Discount Max %" type="number" value={settings.upgrade_discount_max_pct} onChange={(v) => setSettings({ ...settings, upgrade_discount_max_pct: parseInt(v) || 0 })} testid="settings-upgrade-pct" />
                                <Field label="Offer Text" value={settings.offer_text} onChange={(v) => setSettings({ ...settings, offer_text: v })} testid="settings-offer" wide />
                                <Field label="Upgrade Offer Text" value={settings.upgrade_offer_text} onChange={(v) => setSettings({ ...settings, upgrade_offer_text: v })} testid="settings-upgrade-text" wide />
                            </div>

                            {/* Razorpay live-keys section — critical for online payments on production */}
                            <div className="mt-8 p-5 bg-[#f8f9fa] rounded-2xl border border-black/5" data-testid="settings-razorpay-block">
                                <div className="flex items-center justify-between flex-wrap gap-2">
                                    <div>
                                        <h3 className="font-serif text-[18px]">Razorpay Live Keys</h3>
                                        <p className="text-[12px] text-[#4a5568] mt-1">Paste your production keys from <a href="https://dashboard.razorpay.com/app/keys" target="_blank" rel="noreferrer" className="text-[#003E4D] underline">dashboard.razorpay.com → API Keys</a>. Saved keys work immediately — no redeploy needed. Secret is never exposed to buyers.</p>
                                    </div>
                                    <span
                                        className={`text-[11px] px-3 py-1.5 rounded-full font-semibold uppercase tracking-[0.16em] ${settings.razorpay_key_id && settings.razorpay_key_secret ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}
                                        data-testid="settings-razorpay-status"
                                    >
                                        {settings.razorpay_key_id && settings.razorpay_key_secret ? "● LIVE" : "○ Not configured"}
                                    </span>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                    <Field label="Razorpay Key ID (rzp_live_… or rzp_test_…)" value={settings.razorpay_key_id || ""} onChange={(v) => setSettings({ ...settings, razorpay_key_id: v.trim() })} testid="settings-razorpay-key-id" wide />
                                    <Field label="Razorpay Key Secret" value={settings.razorpay_key_secret || ""} onChange={(v) => setSettings({ ...settings, razorpay_key_secret: v.trim() })} testid="settings-razorpay-key-secret" wide type="password" />
                                </div>
                                <label className="mt-4 flex items-center gap-2 text-[13px] text-[#1a1a1a]">
                                    <input
                                        type="checkbox"
                                        checked={!!settings.razorpay_enabled}
                                        onChange={(e) => setSettings({ ...settings, razorpay_enabled: e.target.checked })}
                                        className="w-4 h-4 accent-[#003E4D]"
                                        data-testid="settings-razorpay-enabled"
                                    />
                                    Show online-payment button on checkout
                                </label>
                            </div>

                            <button onClick={updateSettings} className="btn-primary mt-6" data-testid="settings-save">Save settings</button>
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
}

const Stat = ({ label, value }) => (
    <div className="bg-white rounded-2xl p-5 border border-black/5">
        <p className="font-serif text-[28px] text-[#003E4D]">{value}</p>
        <p className="text-[11.5px] uppercase tracking-[0.18em] text-[#9CA3AF] mt-1">{label}</p>
    </div>
);

// LeadRow — expandable row that surfaces ALL lead details (esp. upgrade fields the
// user complained were missing: current_brand, current_model, year_of_purchase,
// warranty_status, working_condition, preferred_clinic, email, message).
const LeadRow = ({ l }) => {
    const [open, setOpen] = useState(false);
    const isUpgrade = l.type === "upgrade";
    const isTrial = l.type === "trial";
    // Build a rich summary depending on lead type so admins see key fields at a glance —
    // no expand needed for the common data (fixes user report: warranty/condition/trial date "missing").
    let summary = l.product_name || l.message || "—";
    let subSummary = null;
    if (isUpgrade) {
        summary = [l.current_brand, l.current_model].filter(Boolean).join(" · ") || "Upgrade lead";
        subSummary = [
            l.year_of_purchase && `${l.year_of_purchase}`,
            l.warranty_status,
            l.working_condition,
        ].filter(Boolean).join(" · ");
    } else if (isTrial) {
        summary = l.product_name || "Trial";
        subSummary = [
            l.preferred_date && `📅 ${l.preferred_date}`,
            l.preferred_time,
            l.city,
        ].filter(Boolean).join(" · ");
    }
    const hasDetails = Boolean(
        l.current_brand || l.current_model || l.year_of_purchase || l.warranty_status ||
        l.working_condition || l.preferred_clinic || l.email || l.message ||
        l.preferred_date || l.preferred_time,
    );
    return (
        <>
            <tr
                className={`border-b border-[#E5E7EB] text-[13.5px] ${hasDetails ? "cursor-pointer hover:bg-[#f8f9fa]" : ""}`}
                data-testid={`lead-row-${l.id}`}
                onClick={() => hasDetails && setOpen((o) => !o)}
            >
                <td className="py-3 pr-4">
                    {l.type === "whatsapp" ? (
                        <span className="px-2 py-1 rounded-full bg-[#25D366]/10 text-[#1f9e4a] text-[11px] uppercase tracking-wider inline-flex items-center gap-1">
                            <MsgIcon size={11} /> WA
                        </span>
                    ) : (
                        <span className="px-2 py-1 rounded-full bg-[#003E4D]/[0.07] text-[#003E4D] text-[11px] uppercase tracking-wider">{String(l.type).replace("_", " ")}</span>
                    )}
                </td>
                <td className="py-3 pr-4 font-medium">
                    {l.name}
                    {hasDetails && <span className="ml-2 text-[10px] text-[#003E4D] uppercase tracking-wider">{open ? "▲" : "▼"}</span>}
                </td>
                <td className="py-3 pr-4 font-mono text-[12.5px]">{l.mobile === "anonymous" ? <span className="text-[#9CA3AF] italic">anonymous click</span> : l.mobile}</td>
                <td className="py-3 pr-4">{l.city || "—"}</td>
                <td className="py-3 pr-4 text-[12.5px]">
                    <div className="font-medium text-[13px]">{summary || "—"}</div>
                    {subSummary && <div className="text-[11.5px] text-[#4a5568] mt-0.5">{subSummary}</div>}
                    <div className="text-[11px] text-[#9CA3AF] mt-0.5">{l.source_page || ""}</div>
                </td>
                <td className="py-3 pr-4 text-[#4a5568]">{new Date(l.created_at).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}</td>
            </tr>
            {open && hasDetails && (
                <tr className="bg-[#f8f9fa] border-b border-[#E5E7EB]" data-testid={`lead-row-details-${l.id}`}>
                    <td colSpan={6} className="p-5">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-3 text-[13px]">
                            {l.email && <Detail label="Email" value={l.email} />}
                            {isUpgrade && <Detail label="Current brand" value={l.current_brand} />}
                            {isUpgrade && <Detail label="Current model" value={l.current_model} />}
                            {isUpgrade && <Detail label="Year of purchase" value={l.year_of_purchase} />}
                            {isUpgrade && <Detail label="Warranty status" value={l.warranty_status} />}
                            {isUpgrade && <Detail label="Working condition" value={l.working_condition} />}
                            {isUpgrade && <Detail label="Preferred clinic" value={l.preferred_clinic} />}
                            {isTrial && <Detail label="Preferred date" value={l.preferred_date} />}
                            {isTrial && <Detail label="Preferred time" value={l.preferred_time} />}
                            {l.product_slug && <Detail label="Product interest" value={`${l.product_name || ""} (${l.product_slug})`} />}
                            {l.message && <Detail label="Message" value={l.message} wide />}
                            {l.utm && Object.keys(l.utm).length > 0 && <Detail label="UTM" value={JSON.stringify(l.utm)} wide />}
                            <Detail label="WhatsApp consent" value={l.whatsapp_consent ? "Yes" : "No"} />
                            <Detail label="Lead ID" value={l.id} wide />
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
};

const Detail = ({ label, value, wide }) => (
    <div className={wide ? "md:col-span-3" : ""}>
        <p className="text-[10.5px] uppercase tracking-[0.18em] text-[#9CA3AF] font-semibold">{label}</p>
        <p className="text-[13.5px] text-[#1a1a1a] mt-0.5 break-words">{value || <span className="text-[#9CA3AF] italic">—</span>}</p>
    </div>
);

const Field = ({ label, value, onChange, type = "text", testid, wide }) => (
    <label className={`block ${wide ? "md:col-span-2" : ""}`}>
        <span className="text-[12px] uppercase tracking-[0.18em] text-[#9CA3AF]">{label}</span>
        <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} className="h-11 w-full px-3 rounded-lg border border-[#E5E7EB] bg-white text-[14px] mt-1 focus:outline-none focus:border-[#003E4D]" data-testid={testid} />
    </label>
);

const CField = ({ label, testid, wide, children }) => (
    <label className={`block ${wide ? "md:col-span-2" : ""}`} data-testid={testid}>
        <span className="text-[12px] uppercase tracking-[0.18em] text-[#9CA3AF]">{label}</span>
        <div className="mt-1">{children}</div>
    </label>
);

const StatusCard = ({ ok, label, detail, icon: Icon }) => (
    <div className={`p-4 rounded-xl border ${ok ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`} data-testid={`status-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
        <div className="flex items-center gap-2">
            {Icon && <Icon size={14} className={ok ? "text-emerald-700" : "text-amber-700"} />}
            <span className={`text-[11px] uppercase tracking-[0.18em] ${ok ? "text-emerald-700" : "text-amber-700"} font-semibold`}>{label}</span>
        </div>
        <p className={`text-[12.5px] mt-1.5 font-medium ${ok ? "text-emerald-900" : "text-amber-900"} break-all`}>{detail}</p>
    </div>
);


// ---------- ClinicsPanel — CRUD for HearClear clinics ----------
function ClinicsPanel() {
    const [clinics, setClinics] = useState([]);
    const [editing, setEditing] = useState(null); // clinic object or null
    const [showForm, setShowForm] = useState(false);

    const load = async () => {
        try {
            const r = await api.get("/admin/clinics");
            setClinics(r.data);
        } catch {
            toast.error("Could not load clinics");
        }
    };

    useEffect(() => { load(); }, []);

    const emptyClinic = {
        name: "",
        slug: "",
        city: "",
        state: "",
        address: "",
        phone: "",
        hours: "Mon–Sun: 10:00 AM – 7:00 PM",
        rating: 4.8,
        sort_order: 0,
    };

    const openNew = () => { setEditing({ ...emptyClinic }); setShowForm(true); };
    const openEdit = (c) => { setEditing({ ...c }); setShowForm(true); };
    const closeForm = () => { setShowForm(false); setEditing(null); };

    const save = async () => {
        if (!editing.name || !editing.city || !editing.address) {
            toast.error("Name, city and address are required.");
            return;
        }
        try {
            if (editing.id) {
                await api.put(`/admin/clinics/${editing.id}`, editing);
                toast.success("Clinic updated");
            } else {
                await api.post("/admin/clinics", editing);
                toast.success("Clinic added");
            }
            closeForm();
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Save failed");
        }
    };

    const remove = async (c) => {
        if (!window.confirm(`Delete ${c.name}? This cannot be undone.`)) return;
        try {
            await api.delete(`/admin/clinics/${c.id}`);
            toast.success("Clinic removed");
            load();
        } catch {
            toast.error("Delete failed");
        }
    };

    return (
        <div data-testid="admin-clinics-panel">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h2 className="font-serif text-[28px]">Clinics ({clinics.length})</h2>
                    <p className="text-[14px] text-[#4a5568] mt-1">Add, edit or remove HearClear clinics that appear on the site and in Arushi&apos;s clinic suggestions.</p>
                </div>
                <button onClick={openNew} className="btn-primary !text-[13px] !py-2.5 !px-5" data-testid="clinic-add-btn">
                    <Plus size={14} /> Add clinic
                </button>
            </div>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                {clinics.map((c) => (
                    <div key={c.id} className="border border-black/5 rounded-xl p-4 flex gap-3 items-start bg-white" data-testid={`admin-clinic-${c.id}`}>
                        <MapPin size={18} className="text-[#003E4D] mt-1 shrink-0" />
                        <div className="flex-1 min-w-0">
                            <p className="font-medium text-[14px]">{c.name}</p>
                            <p className="text-[12.5px] text-[#4a5568] leading-snug mt-0.5">{c.address}</p>
                            <p className="text-[11.5px] text-[#9CA3AF] mt-1">{c.city}, {c.state} · {c.phone}</p>
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <button onClick={() => openEdit(c)} className="text-[11.5px] text-[#003E4D] hover:underline inline-flex items-center gap-1" data-testid={`clinic-edit-${c.id}`}>
                                <Edit2 size={12} /> Edit
                            </button>
                            <button onClick={() => remove(c)} className="text-[11.5px] text-red-500 hover:underline inline-flex items-center gap-1" data-testid={`clinic-delete-${c.id}`}>
                                <Trash2 size={12} /> Delete
                            </button>
                        </div>
                    </div>
                ))}
                {clinics.length === 0 && <p className="col-span-2 py-12 text-center text-[#9CA3AF]">No clinics yet. Click &ldquo;Add clinic&rdquo; to create one.</p>}
            </div>

            {showForm && editing && (
                <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={closeForm} data-testid="clinic-form-modal">
                    <div className="bg-white rounded-3xl max-w-[640px] w-full max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
                        <h3 className="font-serif text-[22px]">{editing.id ? "Edit clinic" : "Add clinic"}</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
                            <Field label="Clinic name *" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} testid="clinic-form-name" wide />
                            <Field label="Slug (optional — auto if blank)" value={editing.slug} onChange={(v) => setEditing({ ...editing, slug: v })} testid="clinic-form-slug" wide />
                            <Field label="City *" value={editing.city} onChange={(v) => setEditing({ ...editing, city: v })} testid="clinic-form-city" />
                            <Field label="State" value={editing.state} onChange={(v) => setEditing({ ...editing, state: v })} testid="clinic-form-state" />
                            <Field label="Full address *" value={editing.address} onChange={(v) => setEditing({ ...editing, address: v })} testid="clinic-form-address" wide />
                            <Field label="Phone" value={editing.phone} onChange={(v) => setEditing({ ...editing, phone: v })} testid="clinic-form-phone" />
                            <Field label="Hours" value={editing.hours} onChange={(v) => setEditing({ ...editing, hours: v })} testid="clinic-form-hours" />
                            <Field label="Rating (0–5)" type="number" value={editing.rating} onChange={(v) => setEditing({ ...editing, rating: parseFloat(v) || 0 })} testid="clinic-form-rating" />
                            <Field label="Sort order" type="number" value={editing.sort_order} onChange={(v) => setEditing({ ...editing, sort_order: parseInt(v) || 0 })} testid="clinic-form-sort" />
                        </div>
                        <div className="mt-6 flex gap-3 justify-end">
                            <button onClick={closeForm} className="btn-secondary !text-[13px] !py-2.5 !px-5" data-testid="clinic-form-cancel">Cancel</button>
                            <button onClick={save} className="btn-primary !text-[13px] !py-2.5 !px-5" data-testid="clinic-form-save">
                                {editing.id ? "Save changes" : "Add clinic"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
